import { useAuth } from '@/store/useAuth';
import { useUpdateUserData } from '@/hooks/use-update-profile';
import { generatePath, useNavigate } from 'react-router';
import { routesApp } from '@/constans/routes';
import React, { useCallback, useEffect } from 'react';
import { FourthStepRegistartionForm } from './components/fourth-step-registartion-form';
import { UploadPhotoRegistartionForm } from './components/upload-photo-registartion-form';
import { MainInfoRegistartionForm } from './components/main-info-registartion-form';
import 'react-toastify/dist/ReactToastify.css';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { useTranslation } from 'react-i18next';
import { useSuccessfulMessage } from '@/store/useSuccessfulMessage';
import WebApp from '@twa-dev/sdk';
import { EmailForm } from '../email-form';
import styles from './form-registration.module.scss'
import { profilesApi } from '@/api';
import { RegistrationEmailFormHeader } from '../registration-email-form-header';
import { requestOTPForProfile, verifyOTPForProfile } from '@/utils/email-profile-actions';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
    step?: string;
    isLoadingOncePage?: boolean;
    onChangeIsLoadingPage?: (bol: boolean) => void;
}

export const FormRegistration: React.FC<Props> = ({ step, isLoadingOncePage, onChangeIsLoadingPage }) => {
    const userData = useAuth((state) => state.getUser());
    const { mutateAsync: updateUserData } = useUpdateUserData();
    const handleUpdateUserData = async (data: any) => {
        if (!userData) return;
        await updateUserData(data);
    };

    const navigateToNextStep = useNavigate();
    const nextStep = useCallback(
        (step: string) => navigateToNextStep(generatePath(routesApp.registration, { stepNumer: step })),
        [navigateToNextStep],
    );
    
    useEffect(() => {
        if (userData && isLoadingOncePage) {
            onChangeIsLoadingPage?.(false);
            // Если пользователь уже отмечен как завершенный - не душим его повторным проходом по регистрации
            // кидаем сразу в ленту
            if (userData.state === 'complete') {
                navigateToNextStep(generatePath(routesApp.browse));
                return;
            }

            // Проверяем, на каком шаге нужно продолжить регистрацию
            if (!userData?.public_name || !userData?.gender || !userData?.dob) {
                nextStep('1');
                return;
            }
            if (!userData?.photos.length) {
                // Если первый этап заполнен, но ссылка всё равно ведёт на него - переходим
                if (step && step === '1') {
                    nextStep(step);
                    return;
                }

                callYaMetrika('registration_step_2');
                nextStep('2');
                return;
            }
            if (!userData?.looks_for || !userData?.min_age || !userData?.max_age || !userData?.purpose) {
                // Если первый и второй этап заполнены, но ссылка всё равно ведёт на них - переходим
                if (step && ['1', '2'].includes(step)) {
                    nextStep(step);
                    return;
                }

                callYaMetrika('registration_step_3');
                nextStep('3');
                return;
            }
            nextStep('3');
        }
    }, []);

    const { t } = useTranslation();

    const completeRegistration = useAuth(state => state.completeRegistration)

    const { setIsOpen } = useSuccessfulMessage(state => state)
 
    const queryClient = useQueryClient()

    const completeForm = async () => {
        await handleUpdateUserData({ event: 'registration_complete' })
        await completeRegistration()
        await queryClient.invalidateQueries({ queryKey: ['profile'] })
        await new Promise<void>((resolve) => {
            setTimeout(() => {
                navigateToNextStep(routesApp.browse)
                setIsOpen(true)
                resolve()
            }, 500)
        })
    }

    const goToEmailStep = async () => nextStep('4')

    switch (step) {
        case '1':
            return (
                <>
                    <MainInfoRegistartionForm
                        userData={userData}
                        onUpdateUserData={handleUpdateUserData}
                        nextStep={() => nextStep('2')} />
                </>
            );
        case '2':
            return (
                <>
                    <UploadPhotoRegistartionForm userData={userData} />
                </>
            );
        case '3':
            return (
                <>
                    <FourthStepRegistartionForm
                        userData={userData}
                        onUpdateUserData={handleUpdateUserData}
                        nextStep={WebApp.initData.length ? goToEmailStep : completeForm} />
                </>
            );
        case '4':
            return (
                <div className={styles.emailFormWrapper}>
                    <EmailForm
                        emailHeader={<RegistrationEmailFormHeader />}
                        useGradient={false}
                        emailFormClass={styles.emailForm}
                        nextBtnStyles={styles.nextBtn}
                        disabledNextBtnStyles={styles.disabledNextBtn}
                        showSkipBtn={true}
                        skipBtnStyles={styles.skipBtn}
                        skipBtnTextColor={'black'}
                        requestOTP={requestOTPForProfile}
                        verifyOTP={verifyOTPForProfile}
                        onLogin={completeForm}
                        onSkip={completeForm} />
                </div>
            )
        default:
            return <>{t('registration.error404')}</>;
    }
};
